import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

// FilterState matches VenueFilterQuery but with all fields optional for frontend state management
export interface FilterState {
  page?: number;
  limit?: number;
  venueType?: string;
  cityId?: string;
  stateId?: string;
  search?: string;
  minPrice?: number;
  maxPrice?: number;
  isApproved?: boolean;
  isActive?: boolean;
  amenities?: Record<string, boolean>;
  minRating?: number;
  sortBy?: string;
}

export interface ActiveFilter {
  key: string;
  label: string;
  value: string;
}

export interface UseVenueFiltersReturn {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  updateFilter: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void;
  clearFilters: () => void;
  activeFilters: ActiveFilter[];
  removeFilter: (key: string) => void;
  hasActiveFilters: boolean;
  setPage: (page: number) => void;
  setType: (type: string | 'ALL') => void;
  setSortBy: (sortBy: string) => void;
}

const VENUE_TYPE_LABELS: Record<string, string> = {
  GAMING: 'Gaming',
  SNOOKER: 'Snooker',
  KIDS_PLAYAREA: 'Kids Play Area',
};

const SORT_LABELS: Record<string, string> = {
  popular: 'Most Popular',
  'price-low': 'Price: Low to High',
  'price-high': 'Price: High to Low',
  rating: 'Highest Rated',
  newest: 'Newest',
};

const DEFAULT_FILTERS: FilterState = {
  page: 1,
  limit: 12,
};

/**
 * Parse URL search params to filter state
 */
function parseUrlToFilters(searchParams: URLSearchParams): FilterState {
  const filters: FilterState = { ...DEFAULT_FILTERS };

  const page = searchParams.get('page');
  if (page) filters.page = parseInt(page, 10);

  const limit = searchParams.get('limit');
  if (limit) filters.limit = parseInt(limit, 10);

  const venueType = searchParams.get('type');
  if (venueType) {
    filters.venueType = venueType;
  }

  const search = searchParams.get('q');
  if (search) filters.search = search;

  const stateId = searchParams.get('state');
  if (stateId) filters.stateId = stateId;

  const cityId = searchParams.get('city');
  if (cityId) filters.cityId = cityId;

  const minPrice = searchParams.get('minPrice');
  if (minPrice) filters.minPrice = parseFloat(minPrice);

  const maxPrice = searchParams.get('maxPrice');
  if (maxPrice) filters.maxPrice = parseFloat(maxPrice);

  const minRating = searchParams.get('rating');
  if (minRating) filters.minRating = parseFloat(minRating);

  const sortBy = searchParams.get('sort');
  if (sortBy) filters.sortBy = sortBy;

  // Parse amenities (comma-separated)
  const amenities = searchParams.get('amenities');
  if (amenities) {
    const amenityList = amenities.split(',').filter(Boolean);
    if (amenityList.length > 0) {
      filters.amenities = {};
      amenityList.forEach((a) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        filters.amenities![a] = true;
      });
    }
  }

  return filters;
}

/**
 * Convert filter state to URL search params
 */
function filtersToUrl(filters: FilterState): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.page && filters.page > 1) params.set('page', String(filters.page));
  if (filters.limit && filters.limit !== 12) params.set('limit', String(filters.limit));
  if (filters.venueType) params.set('type', filters.venueType);
  if (filters.search) params.set('q', filters.search);
  if (filters.stateId) params.set('state', filters.stateId);
  if (filters.cityId) params.set('city', filters.cityId);
  if (filters.minPrice !== undefined) params.set('minPrice', String(filters.minPrice));
  if (filters.maxPrice !== undefined) params.set('maxPrice', String(filters.maxPrice));
  if (filters.minRating !== undefined) params.set('rating', String(filters.minRating));
  if (filters.sortBy) params.set('sort', filters.sortBy);

  // Amenities as comma-separated
  if (filters.amenities) {
    const activeAmenities = Object.entries(filters.amenities)
      .filter(([, value]) => value)
      .map(([key]) => key);
    if (activeAmenities.length > 0) {
      params.set('amenities', activeAmenities.join(','));
    }
  }

  return params;
}

export function useVenueFilters(initialFilters?: FilterState): UseVenueFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize from URL on first render
  const [filters, setFiltersState] = useState<FilterState>(() => {
    const urlFilters = parseUrlToFilters(searchParams);
    return {
      ...DEFAULT_FILTERS,
      ...urlFilters,
      ...initialFilters,
    };
  });

  // Sync filters to URL whenever they change
  useEffect(() => {
    const newParams = filtersToUrl(filters);
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();

    // Only update if different to avoid infinite loops
    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [filters, searchParams, setSearchParams]);

  const setFilters = useCallback((newFilters: FilterState) => {
    setFiltersState(newFilters);
  }, []);

  const updateFilter = useCallback(
    <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
      setFiltersState((prev) => {
        const updated = { ...prev, [key]: value };

        // Reset city when state changes
        if (key === 'stateId') {
          updated.cityId = undefined;
        }

        // Reset page when filters change (except for page itself)
        if (key !== 'page') {
          updated.page = 1;
        }

        return updated;
      });
    },
    []
  );

  const clearFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
  }, []);

  const removeFilter = useCallback((key: string) => {
    setFiltersState((prev) => {
      const updated = { ...prev };

      if (key.startsWith('amenity-')) {
        const amenityId = key.replace('amenity-', '');
        if (updated.amenities) {
          const { [amenityId]: _, ...rest } = updated.amenities;
          updated.amenities = Object.keys(rest).length > 0 ? rest : undefined;
        }
      } else {
        delete updated[key as keyof FilterState];
      }

      // Reset page when filters change
      updated.page = 1;

      return updated;
    });
  }, []);

  const setPage = useCallback((page: number) => {
    setFiltersState((prev) => ({ ...prev, page }));
  }, []);

  const setType = useCallback((type: string | 'ALL') => {
    setFiltersState((prev) => ({
      ...prev,
      venueType: type === 'ALL' ? undefined : type,
      page: 1,
    }));
  }, []);

  const setSortBy = useCallback((sortBy: string) => {
    setFiltersState((prev) => ({
      ...prev,
      sortBy: sortBy || undefined,
      page: 1,
    }));
  }, []);

  const activeFilters = useMemo<ActiveFilter[]>(() => {
    const active: ActiveFilter[] = [];

    if (filters.search) {
      active.push({
        key: 'search',
        label: 'Search',
        value: filters.search,
      });
    }

    if (filters.venueType) {
      active.push({
        key: 'venueType',
        label: 'Type',
        value: VENUE_TYPE_LABELS[filters.venueType] || filters.venueType,
      });
    }

    if (filters.stateId) {
      active.push({
        key: 'stateId',
        label: 'State',
        value: filters.stateId, // Will be replaced with name in component
      });
    }

    if (filters.cityId) {
      active.push({
        key: 'cityId',
        label: 'City',
        value: filters.cityId, // Will be replaced with name in component
      });
    }

    if (filters.minPrice !== undefined) {
      active.push({
        key: 'minPrice',
        label: 'Min Price',
        value: `₹${filters.minPrice}`,
      });
    }

    if (filters.maxPrice !== undefined) {
      active.push({
        key: 'maxPrice',
        label: 'Max Price',
        value: `₹${filters.maxPrice}`,
      });
    }

    if (filters.minRating !== undefined) {
      active.push({
        key: 'minRating',
        label: 'Rating',
        value: `${filters.minRating}+ stars`,
      });
    }

    if (filters.sortBy) {
      active.push({
        key: 'sortBy',
        label: 'Sort',
        value: SORT_LABELS[filters.sortBy] || filters.sortBy,
      });
    }

    // Add amenity filters
    if (filters.amenities) {
      Object.entries(filters.amenities).forEach(([key, value]) => {
        if (value) {
          active.push({
            key: `amenity-${key}`,
            label: 'Amenity',
            value: key.replace(/([A-Z])/g, ' $1').trim(),
          });
        }
      });
    }

    return active;
  }, [filters]);

  const hasActiveFilters = useMemo(() => {
    const { page: _page, limit: _limit, ...rest } = filters;
    return Object.keys(rest).some((key) => {
      const value = rest[key as keyof typeof rest];
      if (key === 'amenities' && typeof value === 'object') {
        return Object.values(value).some(Boolean);
      }
      return value !== undefined && value !== '';
    });
  }, [filters]);

  return {
    filters,
    setFilters,
    updateFilter,
    clearFilters,
    activeFilters,
    removeFilter,
    hasActiveFilters,
    setPage,
    setType,
    setSortBy,
  };
}
